import React, { useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Plus, Minus, Globe, Headphones, Sun, Moon } from "lucide-react";
import Logo from "./Logo";
import MarketTicker from "./MarketTicker";
import { useSite } from "@/context/SiteContext";
import { useTheme } from "@/context/ThemeContext";

// Dukascopy-style category mega menu. Items are internal (to) or external via Central Link Manager (key).
const DEFAULT_MENU = [
  {
    label: "Forex",
    columns: [
      { title: "Trading", items: [{ l: "Why Cresent", to: "/conditions" }, { l: "ECN Accounts", to: "/accounts" }, { l: "Account Types", to: "/accounts" }, { l: "Trading Conditions", to: "/conditions" }] },
      { title: "Platforms", items: [{ l: "MetaTrader 4", to: "/platforms" }, { l: "MetaTrader 5", to: "/platforms" }, { l: "Cresent WebTrader", to: "/platforms" }, { l: "Automated Trading", to: "/platforms" }] },
      { title: "Range of Markets", items: [{ l: "Forex", to: "/markets" }, { l: "Metals", to: "/markets" }, { l: "Commodities", to: "/markets" }] },
    ],
    cta: { l: "Open Live Account", key: "live_register" },
  },
  {
    label: "CFD",
    columns: [
      { title: "Range of Markets", items: [{ l: "Indices", to: "/markets" }, { l: "Stocks", to: "/markets" }, { l: "Commodities", to: "/markets" }] },
      { title: "Conditions", items: [{ l: "Trading Conditions", to: "/conditions" }, { l: "Leverage & Margin", to: "/conditions" }] },
      { title: "Platforms", items: [{ l: "MT4 / MT5", to: "/platforms" }, { l: "WebTrader", to: "/platforms" }] },
    ],
  },
  {
    label: "Crypto",
    columns: [
      { title: "Crypto Trading", items: [{ l: "Crypto Markets", to: "/markets" }, { l: "Trading Conditions", to: "/conditions" }] },
      { title: "Wallet & Funding", items: [{ l: "Wallet", key: "wallet" }, { l: "Deposit", key: "deposit" }, { l: "Withdrawal", key: "withdrawal" }] },
    ],
  },
  {
    label: "Bank",
    columns: [
      { title: "Accounts", items: [{ l: "Multi-Currency Account", key: "client_portal" }, { l: "Business Accounts", key: "client_portal" }] },
      { title: "Services", items: [{ l: "Cards", key: "payment_gateway" }, { l: "Payments", key: "deposit" }, { l: "Wealth Management", key: "client_portal" }] },
    ],
  },
  {
    label: "Market Research",
    columns: [
      { title: "Research", items: [{ l: "News & Analysis", to: "/news" }, { l: "Trading Ideas", to: "/news" }, { l: "Technical Analysis", to: "/news" }] },
      { title: "Tools & Learning", items: [{ l: "Economic Calendar", to: "/news" }, { l: "Education Center", to: "/education" }, { l: "FAQ", to: "/faq" }] },
    ],
  },
  {
    label: "Company",
    columns: [
      { title: "About Cresent", items: [{ l: "Promotions", to: "/promotions" }, { l: "FAQ", to: "/faq" }, { l: "Contact Us", to: "/contact" }] },
    ],
  },
];

export default function PublicHeader() {
  const { trackAndGo, menu } = useSite();
  const { theme, toggle } = useTheme();
  const MENU = (menu && menu.length) ? menu : DEFAULT_MENU;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [mobileExpand, setMobileExpand] = useState(null);
  const timer = useRef();

  const enter = (label) => { clearTimeout(timer.current); setActive(label); };
  const leave = () => { timer.current = setTimeout(() => setActive(null), 120); };

  const ItemLink = ({ it, onClick }) =>
    it.to ? (
      <NavLink to={it.to} onClick={onClick} className="block px-3 py-2 text-sm text-slate-300 hover:text-[#D8AE4E] hover:bg-[#102653] rounded transition-colors" data-testid={`menu-item-${it.l.toLowerCase().replace(/\s+/g, "-")}`}>
        {it.l}
      </NavLink>
    ) : (
      <button onClick={() => { trackAndGo(it.key); onClick && onClick(); }} className="block w-full text-left px-3 py-2 text-sm text-slate-300 hover:text-[#D8AE4E] hover:bg-[#102653] rounded transition-colors" data-testid={`menu-item-${it.l.toLowerCase().replace(/\s+/g, "-")}`}>
        {it.l}
      </button>
    );

  return (
    <header className="sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="bg-[#030A1F] border-b border-[#203E70]/60 text-xs">
        <div className="cb-container flex items-center justify-between h-9">
          <div className="flex items-center gap-2 text-slate-400">
            <Globe size={13} className="gold-text" />
            <span>Swiss Institutional Markets</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => trackAndGo("demo_register")} className="text-slate-400 hover:text-[#D8AE4E] transition-colors" data-testid="util-demo">Open Demo</button>
            <button onClick={() => trackAndGo("support")} className="hidden sm:inline-flex items-center gap-1 text-slate-400 hover:text-[#D8AE4E] transition-colors" data-testid="util-support"><Headphones size={13} /> Support</button>
            <button onClick={toggle} className="inline-flex items-center gap-1 text-slate-400 hover:text-[#D8AE4E] transition-colors" data-testid="theme-toggle" aria-label="Toggle light or dark mode">{theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}<span className="hidden sm:inline">{theme === "dark" ? "Light" : "Dark"}</span></button>
            <span className="hidden sm:inline text-slate-500 font-mono">EN</span>
          </div>
        </div>
      </div>

      <MarketTicker />

      {/* Main nav */}
      <div className="bg-[#06122E]/95 backdrop-blur-md border-b border-[#D8AE4E]/20">
        <div className="cb-container flex items-center justify-between h-16">
          <Link to="/" data-testid="header-home-link"><Logo /></Link>

          <nav className="hidden lg:flex items-center">
            {MENU.map((m) => (
              <div key={m.label} className="relative" onMouseEnter={() => enter(m.label)} onMouseLeave={leave}>
                <button className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors ${active === m.label ? "text-[#D8AE4E]" : "text-slate-200 hover:text-white"}`} data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {m.label} <ChevronDown size={13} className={`transition-transform ${active === m.label ? "rotate-180" : ""}`} />
                </button>
                {active === m.label && (
                  <div className="absolute left-0 top-full pt-3" onMouseEnter={() => enter(m.label)} onMouseLeave={leave}>
                    <div className="cb-card !bg-[#0B1D40] p-5 flex gap-6 shadow-2xl" style={{ minWidth: m.columns.length * 190 + (m.cta ? 200 : 0) }}>
                      {m.columns.map((col) => (
                        <div key={col.title} className="min-w-[170px]">
                          <p className="overline mb-2">{col.title}</p>
                          <div className="space-y-0.5">
                            {col.items.map((it) => <ItemLink key={it.l} it={it} onClick={() => setActive(null)} />)}
                          </div>
                        </div>
                      ))}
                      {m.cta && (
                        <div className="w-[190px] border-l border-[#203E70] pl-5 flex flex-col justify-between">
                          <div>
                            <p className="text-white font-serif text-lg leading-tight">Start trading today</p>
                            <p className="text-slate-400 text-xs mt-1">Open your account in minutes.</p>
                          </div>
                          <button onClick={() => { trackAndGo(m.cta.key); setActive(null); }} className="btn-gold px-4 py-2.5 text-sm mt-4" data-testid="mega-cta">{m.cta.l}</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button onClick={() => trackAndGo("client_login")} className="btn-outline-gold px-4 py-2 text-sm" data-testid="header-login-btn">Login</button>
            <button onClick={() => trackAndGo("live_register")} className="btn-gold px-4 py-2 text-sm" data-testid="header-open-account-btn">Open Account</button>
          </div>

          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-[#203E70] bg-[#06122E] max-h-[70vh] overflow-y-auto admin-scroll">
            {MENU.map((m) => (
              <div key={m.label} className="border-b border-[#203E70]/60">
                <button onClick={() => setMobileExpand(mobileExpand === m.label ? null : m.label)} className="w-full flex items-center justify-between px-4 py-3 text-slate-100" data-testid={`mnav-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {m.label} {mobileExpand === m.label ? <Minus size={16} /> : <Plus size={16} />}
                </button>
                {mobileExpand === m.label && (
                  <div className="px-4 pb-3 space-y-3">
                    {m.columns.map((col) => (
                      <div key={col.title}>
                        <p className="overline mb-1">{col.title}</p>
                        {col.items.map((it) => <ItemLink key={it.l} it={it} onClick={() => setOpen(false)} />)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-3 p-4">
              <button onClick={() => trackAndGo("client_login")} className="btn-outline-gold px-4 py-2.5 text-sm flex-1">Login</button>
              <button onClick={() => trackAndGo("live_register")} className="btn-gold px-4 py-2.5 text-sm flex-1">Open Account</button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
